import { z } from "@brains/utils/zod";
import type {
  SiteLayoutInfo,
  SiteMetadata,
  SiteMetadataCTA,
  SiteMetadataSection,
} from "@rizom/site";
import { NavigationItemSchema } from "./routes";

export type {
  SiteLayoutInfo,
  SiteMetadata,
  SiteMetadataCTA,
  SiteMetadataSection,
} from "@rizom/site";

export const SITE_METADATA_GET_CHANNEL = "site:metadata:get";
export const SITE_METADATA_UPDATED_CHANNEL = "site:metadata:updated";

export const siteMetadataCTASchema: z.ZodObject<{
  heading: z.ZodString;
  buttonText: z.ZodString;
  buttonLink: z.ZodString;
}> = z.object({
  heading: z.string().describe("Main CTA heading text"),
  buttonText: z.string().describe("Call-to-action button text"),
  buttonLink: z.string().describe("URL or anchor for the CTA button"),
});

/** Per-section homepage descriptors — short editorial blurbs displayed
 * beneath each section title (e.g. under "Essays"). Keys match section ids
 * the homepage template knows about ("essays", "presentations", "about", …).
 */
export const siteMetadataSectionSchema: z.ZodObject<{
  blurb: z.ZodOptional<z.ZodString>;
}> = z.object({
  blurb: z
    .string()
    .optional()
    .describe("Short italic subtitle under the section title"),
});

type SiteMetadataSchema = z.ZodObject<{
  represents: z.ZodDefault<z.ZodEnum<{ brain: "brain"; anchor: "anchor" }>>;
  title: z.ZodString;
  description: z.ZodString;
  url: z.ZodOptional<z.ZodString>;
  copyright: z.ZodOptional<z.ZodString>;
  logo: z.ZodOptional<z.ZodBoolean>;
  themeMode: z.ZodOptional<z.ZodEnum<{ light: "light"; dark: "dark" }>>;
  analyticsScript: z.ZodOptional<z.ZodString>;
  cta: z.ZodOptional<typeof siteMetadataCTASchema>;
  sections: z.ZodOptional<
    z.ZodRecord<z.ZodString, typeof siteMetadataSectionSchema>
  >;
}>;

/** Plain site metadata consumed by site renderers. */
export const siteMetadataSchema: SiteMetadataSchema = z.object({
  represents: z
    .enum(["brain", "anchor"])
    .default("anchor")
    .describe("Identity represented by the site"),
  title: z.string().describe("The site's title"),
  description: z.string().describe("The site's description"),
  url: z.string().optional().describe("Canonical site URL"),
  copyright: z.string().optional().describe("Copyright notice text"),
  logo: z
    .boolean()
    .optional()
    .describe("Whether to display logo instead of title text in header"),
  themeMode: z
    .enum(["light", "dark"])
    .optional()
    .describe("Default theme mode"),
  analyticsScript: z.string().optional().describe("Analytics script HTML"),
  cta: siteMetadataCTASchema
    .optional()
    .describe("Call-to-action configuration"),
  sections: z
    .record(z.string(), siteMetadataSectionSchema)
    .optional()
    .describe(
      "Optional per-section blurbs, keyed by section id (e.g. 'essays', 'presentations', 'about'). Used by homepage templates that render editorial section headers.",
    ),
});

/**
 * Accepted input for {@link siteMetadataSchema}. Differs from `SiteMetadata` in
 * that defaulted fields (`represents`) may be omitted. Consumers that validate
 * caller-supplied metadata — plugin config, job payloads — must type their input
 * with this, not with the parsed output.
 */
export type SiteMetadataInput = z.input<typeof siteMetadataSchema>;

type SiteMetadataSchemaOutput = z.infer<typeof siteMetadataSchema>;
type SiteMetadataCTASchemaOutput = z.infer<typeof siteMetadataCTASchema>;
type SiteMetadataSectionSchemaOutput = z.infer<
  typeof siteMetadataSectionSchema
>;

function expectSiteMetadata(value: SiteMetadataSchemaOutput): SiteMetadata {
  return value;
}

function expectSiteMetadataCTA(
  value: SiteMetadataCTASchemaOutput,
): SiteMetadataCTA {
  return value;
}

function expectSiteMetadataSection(
  value: SiteMetadataSectionSchemaOutput,
): SiteMetadataSection {
  return value;
}

void expectSiteMetadata;
void expectSiteMetadataCTA;
void expectSiteMetadataSection;

const socialLinkSchema: z.ZodObject<{
  platform: z.ZodEnum<{
    github: "github";
    instagram: "instagram";
    linkedin: "linkedin";
    email: "email";
    website: "website";
  }>;
  url: z.ZodString;
  label: z.ZodOptional<z.ZodString>;
}> = z.object({
  platform: z
    .enum(["github", "instagram", "linkedin", "email", "website"])
    .describe("Social media platform"),
  url: z.string().describe("Profile or contact URL"),
  label: z.string().optional().describe("Optional display label"),
});

/**
 * Complete site information passed to layout components.
 *
 * Layout info is site metadata plus the navigation and social links the build
 * resolves, with `copyright` promoted to required. It is derived from
 * {@link siteMetadataSchema} rather than restated so the shared fields cannot
 * drift — the TypeScript side already models this as
 * `interface SiteLayoutInfo extends SiteMetadata`.
 */
export const siteLayoutInfoSchema: ReturnType<
  typeof siteMetadataSchema.extend<{
    copyright: z.ZodString;
    navigation: z.ZodObject<{
      primary: z.ZodArray<typeof NavigationItemSchema>;
      secondary: z.ZodArray<typeof NavigationItemSchema>;
    }>;
    socialLinks: z.ZodOptional<z.ZodArray<typeof socialLinkSchema>>;
  }>
> = siteMetadataSchema.extend({
  copyright: z.string(),
  navigation: z.object({
    primary: z.array(NavigationItemSchema),
    secondary: z.array(NavigationItemSchema),
  }),
  socialLinks: z
    .array(socialLinkSchema)
    .optional()
    .describe("Social media links from profile metadata"),
});

type SiteLayoutInfoSchemaOutput = z.infer<typeof siteLayoutInfoSchema>;
function expectSiteLayoutInfo(
  value: SiteLayoutInfoSchemaOutput,
): SiteLayoutInfo {
  return value;
}
void expectSiteLayoutInfo;
